import styled from "@emotion/styled";
import { padding } from "polished";
import React, { useRef, useEffect } from "react";

import { Link } from "@brexhq/next/src/components/shared/Link";

import type { INavLink } from "../../types";

const DefaultLink = styled(Link)(({ theme }) => ({
  ...theme.typography.BodySmallSB,
  border: "1px solid transparent",
  borderRadius: theme.borders.radius.default,
  ...padding(theme.spacing.LRG.xxsml / 2, theme.spacing.LRG.xxsml),
  whiteSpace: "nowrap",
  "&:focus": {
    outline: "none",
    borderColor: theme.colors.content.inverse.secondary,
  },
}));

const OutlineLink = styled(Link)(({ theme }) => ({
  ...theme.typography.BodySmallM,
  ...padding(theme.spacing.LRG.xxsml, theme.spacing.LRG.xsml),
  display: "flex",
  alignItem: "center",
  color: theme.colors.accent.primary.background.default,
  border: `1px solid ${theme.colors.accent.primary.background.default}`,
  borderRadius: theme.borders.radius.default,
  whiteSpace: "nowrap",
  "&:hover": {
    color: theme.colors.accent.primary.background.default,
  },
  "&:focus": {
    outline: "none",
    boxShadow: `0 0 0 1px ${theme.colors.focus}`,
    borderColor: theme.colors.focus,
  },
}));

interface Props extends Omit<INavLink, "_type"> {
  isFocused?: boolean;
  onActivate: () => void;
  modifier?: "primary" | "outline" | "arrow";
}

export const NavLink = ({
  modifier,
  text,
  link,
  isFocused,
  onActivate,
}: Props) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const props = {
    to: link.to || link.toExternal || link.hash || "#",
    noFollow: link?.noFollow,
    ref: linkRef,
    label: text,
    component: "navLink",
    role: "menuitem",
    tabIndex: isFocused ? 0 : -1,
    onMouseEnter: () => onActivate(),
    onFocus: () => onActivate(),
  };

  useEffect(() => {
    if (isFocused) {
      linkRef.current?.focus();
    }
  }, [isFocused]);

  switch (modifier) {
    case "outline":
      return (
        <OutlineLink {...props} modifier="unstyled">
          {text}
        </OutlineLink>
      );
    case "primary":
    case "arrow":
      return (
        <Link
          {...props}
          modifier={modifier}
          style={{ whiteSpace: "nowrap" }}
          size={{
            LRG: "DEVIATED_NAV_SIZE",
            MED: "DEVIATED_NAV_SIZE",
            SML: "DEVIATED_NAV_SIZE",
          }}
        />
      );
    default:
      return (
        <DefaultLink {...props} modifier="unstyled">
          {text}
        </DefaultLink>
      );
  }
};
